/**
 * This is a NavigationRailHorizontal component. This component displays the NavigationRail as a carousel rather than as a group.
 *
 * @module views/components/NavigationRailHorizontal
 * @memberof -Common
 */
import './NavigationRailHorizontal.scss';

import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import SwiperCore, {
  A11y,
  Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import palette_variables from 'web-styles/src/_exports.module.scss';

import Button from '@ulta/core/components/Button/Button';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { handleSwiperImpressionEvent } from '@ulta/core/utils/datacaptureAggregator/datacaptureAggregator';

import carouselUtils, { handleInactiveSwiperSlides, handleSwiperSlideChange } from '@ulta/utils/carouselUtils/carouselUtils';

import NavigationCard from '../NavigationCard/NavigationCard';

SwiperCore.use( [Navigation, Keyboard, A11y] );

let swiperCounter = 1;

/**
 * Represents a NavigationRailHorizontal component
 *
 * @method
 * @param {NavigationRailHorizontalProps} props - React properties passed from composition
 * @returns NavigationRailHorizontal
 */
export const NavigationRailHorizontal = function( props ){
  const {
    backgroundColor,
    items,
    carouselAccessibility,
    carouselDetailsAccessibility,
    nextClickAction,
    previousClickAction,
    previousAccessibility,
    nextAccessibility,
    outlineOption,
    shopAllLabel,
    shopAllAction,
    title,
    root,
    rootMargin,
    threshold
  } = props;
  const { breakpoint } = useDeviceInflection();
  const carouselRef = useRef( null );
  const railRef = useRef( null );
  const [isLoaded, setIsLoaded] = useState( false );

  const isXLargeDevice = breakpoint.CURRENT_BREAKPOINT === 'XL';
  const withShopAll = shopAllAction && shopAllLabel;
  const spaceBetween = isXLargeDevice ? 16 + 1 : 8 + 1; // Adding 1px because Swiper doesn't account for border widths correctly
  const formattedCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const formattedCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  swiperCounter += 1;

  useIntersectionObserver(
    railRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleSwiperImpressionEvent( props, carouselRef )
  );

  return (
    <div className='NavigationRailHorizontal'
      ref={ railRef }
      role='region'
      aria-label={ formattedCarouselAccessibility }
    >
      <div className='NavigationRailHorizontal__container'>
        { carouselAccessibility && (
          <p className='NavigationRailHorizontal__carousel-accessibility sr-only'>
            { formattedCarouselDetailsAccessibility }
          </p>
        ) }
        <div className={ `NavigationRailHorizontal__pagination NavigationRailHorizontal__pagination-${swiperCounter}` }>
          <Button
            variant='navigation'
            iconImage='CaretBack'
            iconSize='lg'
            className='NavigationRailHorizontal__pagination-button NavigationRailHorizontal__pagination-button--back'
            ariaHiddenIcon={ true }
            ariaLabel={ previousAccessibility }
            onClick={ handleSwiperSlideChange(
              null,
              previousClickAction,
              carouselRef,
              handleSwiperImpressionEvent( props, carouselRef ),
              railRef?.current
            ) }
          />
          <Button
            variant='navigation'
            iconImage='CaretForward'
            iconSize='lg'
            className={ 'NavigationRailHorizontal__pagination-button NavigationRailHorizontal__pagination-button--forward' }
            ariaHiddenIcon={ true }
            ariaLabel={ nextAccessibility }
            onClick={ handleSwiperSlideChange(
              nextClickAction,
              null,
              carouselRef,
              handleSwiperImpressionEvent( props, carouselRef ),
              railRef?.current
            ) }
          />
        </div>
        <Swiper
          freeMode
          preloadImages
          ref={ carouselRef }
          speed={ 600 }
          spaceBetween={ spaceBetween }
          watchOverflow={ true }
          watchSlidesProgress={ true }
          watchSlidesVisibility={ true }
          onSwiper={ ( swiper ) => {
            setIsLoaded( true );
            handleInactiveSwiperSlides( swiper );
          } }
          onSlideChange={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
          navigation={ {
            prevEl: `.NavigationRailHorizontal__pagination-${swiperCounter} .NavigationRailHorizontal__pagination-button--back`,
            nextEl: `.NavigationRailHorizontal__pagination-${swiperCounter} .NavigationRailHorizontal__pagination-button--forward`,
            disabledClass: 'NavigationRailHorizontal__pagination--disabled',
            hiddenClass: 'NavigationRailHorizontal__pagination--hidden'
          } }
          breakpoints={
            {
              [palette_variables.breakpointLG]: {
                slidesPerView: 9,
                slidesPerGroup: 9
              },
              [palette_variables.breakpointMD]: {
                slidesPerView: 8,
                slidesPerGroup: 8
              },
              [palette_variables.breakpointSM]: {
                slidesPerView: 3.4,
                slidesPerGroup: 3
              }
            }
          }
        >
          { isLoaded && withShopAll && (
            <SwiperSlide>
              <NavigationCard
                shopAllLabel={ shopAllLabel }
                shopAllAction={ shopAllAction }
                outlineOption={ outlineOption }
                backgroundColor={ backgroundColor }
              />
            </SwiperSlide>
          ) }
          { isLoaded && (
            items?.map( ( item, index ) => (
              <SwiperSlide key={ index }>
                <NavigationCard
                  { ...item }
                  outlineOption={ outlineOption }
                  backgroundColor={ backgroundColor }
                />
              </SwiperSlide>
            ) )
          ) }
        </Swiper>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef NavigationRailHorizontalProps
 * @type {object}
 * @property {array} items - Sets the items to be displayed as NavigationCards for the rail.
 * @property {string} shopAllLabel - Sets the all shop label.
 * @property {object} shopAllAction - Sets the shop all action.
 * @property {object} outlineOption - True if the Navigation Cards should have an outline; otherwise, false.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {string} nextClickAction - Sets the action for previous button.
 * @property {string} previousClickAction - Sets the action for next button.
 * @property {string} carouselAccessibility - Value for aria label for carousel
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} title - Sets the title for CompactDealRail list.
 * @property {string} backgroundColor - Sets the background color for the navigation card.
 */
export const propTypes = {
  items: PropTypes.array,
  shopAllLabel: PropTypes.string,
  shopAllAction: PropTypes.object,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string,
  nextClickAction: PropTypes.object,
  previousClickAction: PropTypes.object,
  carouselAccessibility: PropTypes.string,
  carouselDetailsAccessibility: PropTypes.string,
  outlineOption: PropTypes.bool,
  title: PropTypes.string,
  backgroundColor: PropTypes.string
};

NavigationRailHorizontal.propTypes = propTypes;

export default NavigationRailHorizontal;
